import { Modal } from './Modal.js';
import { resourceLabel } from './schema.js';
import type { Economy } from './Economy.js';
import type { GameConfig, ShopOffer } from '../types.js';

interface ShopWindowOptions {
    config: GameConfig;
    economy: Economy;
}

export class ShopWindow {
    private readonly config: GameConfig;
    private readonly economy: Economy;
    private readonly modal: Modal;
    private readonly list: HTMLElement;

    constructor({ config, economy }: ShopWindowOptions) {
        this.config = config;
        this.economy = economy;

        this.modal = new Modal({ title: config.shop?.title || 'SHOP', variant: 'shop' });
        this.list = document.createElement('div');
        this.list.className = 'shop-list';
        this.modal.body.appendChild(this.list);
    }

    open(): void {
        this.render();
        this.modal.open();
    }

    close(): void {
        this.modal.close();
    }

    toggle(): void {
        if (this.modal.isOpen) this.close();
        else this.open();
    }

    private render(): void {
        this.list.innerHTML = '';
        const offers = this.config.shop?.offers ?? [];

        if (!offers.length) {
            const empty = document.createElement('p');
            empty.className = 'shop-empty';
            empty.textContent = 'Nothing for sale.';
            this.list.appendChild(empty);
            return;
        }

        offers.forEach((offer) => this.list.appendChild(this.renderOffer(offer)));
    }

    private renderOffer(offer: ShopOffer): HTMLElement {
        const row = document.createElement('div');
        row.className = 'shop-offer';

        const name = document.createElement('span');
        name.className = 'shop-offer__name';
        name.textContent = `${offer.quantity} ${resourceLabel(this.config, offer.resource)}`;
        row.appendChild(name);

        const price = document.createElement('span');
        price.className = 'shop-offer__price';
        price.textContent = `${offer.price} ${resourceLabel(this.config, this.config.currency ?? '')}`;
        row.appendChild(price);

        const currency = this.config.currency ?? '';
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'shop-offer__buy';
        button.textContent = 'BUY';
        button.disabled = !this.economy.canAfford([{ resource: currency, quantity: offer.price }]);
        button.addEventListener('click', () => {
            if (!this.economy.spend([{ resource: currency, quantity: offer.price }])) return;
            this.economy.add(offer.resource, offer.quantity);
            this.render();
        });
        row.appendChild(button);

        return row;
    }
}
